/**
 * HtmlContent — renders sanitized HTML content for post/comment text.
 *
 * Falls back to plain text rendering when the input contains no HTML markup.
 * Sanitizes all HTML before rendering: strips scripts, event handlers,
 * javascript: URLs, and allows iframe embeds only from approved domains.
 * Hashtags and @mentions inside the body stay clickable, matching the
 * plain-text renderer.
 */
import { useCallback, useMemo, type KeyboardEvent, type MouseEvent } from 'react';
import { sanitizeHtml, containsHtml } from '../../utils/sanitizeHtml';
import {
  HASHTAG_ATTR,
  MENTION_AMBIGUOUS_ATTR,
  MENTION_ATTR,
  MENTION_ID_ATTR,
  linkifyRichText,
} from '../../utils/linkifyRichText';
import { moveEmbedsAfterText } from '../../utils/richTextEmbeds';
import { MentionText } from './MentionLink';
import { useHashtagNavigate, useMentionNavigate } from './richTextNavigation';
import type { MentionIndex } from '@wlb/shared/utils/mentions';
import './richText.css';

interface HtmlContentProps {
  /** Raw text that may contain HTML markup. */
  html: string;
  /** CSS class for the container. */
  className?: string;
  /** Identities known for this post/comment, from `buildMentionIndex`. */
  mentions?: MentionIndex;
}

export function HtmlContent({ html, className, mentions }: HtmlContentProps) {
  const navigateToHashtag = useHashtagNavigate();
  const navigateToMention = useMentionNavigate();

  const sanitized = useMemo(() => {
    if (!html) return '';
    if (!containsHtml(html)) return '';
    // Embeds move below the caption before the text is linkified: the API
    // appends hashtags after the embed, and they belong with the caption.
    return linkifyRichText(moveEmbedsAfterText(sanitizeHtml(html)), mentions);
  }, [html, mentions]);

  /** Single delegated handler — the body can hold any number of links. */
  const activate = useCallback(
    (target: EventTarget | null): boolean => {
      const element = target instanceof Element
        ? target.closest(`[${HASHTAG_ATTR}], [${MENTION_ATTR}]`)
        : null;
      if (!element) return false;

      const hashtag = element.getAttribute(HASHTAG_ATTR);
      if (hashtag) {
        navigateToHashtag(hashtag);
        return true;
      }
      if (element.hasAttribute(MENTION_ATTR)) {
        // Identity was resolved when the body was linkified.
        navigateToMention({
          userGuid: element.getAttribute(MENTION_ID_ATTR),
          ambiguous: element.hasAttribute(MENTION_AMBIGUOUS_ATTR),
        });
        return true;
      }
      return false;
    },
    [navigateToHashtag, navigateToMention],
  );

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (activate(event.target)) event.stopPropagation();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (activate(event.target)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  if (!html) return null;

  // No HTML detected → plain text, where the same parser handles the links
  // (newlines are preserved by CSS white-space: pre-line).
  if (!sanitized) {
    return (
      <p className={className} style={{ whiteSpace: 'pre-line' }}>
        <MentionText text={html} mentions={mentions} />
      </p>
    );
  }

  return (
    // Keyboard access for the injected links; the container itself is not a
    // control, so it stays out of the tab order.
    <div
      className={className}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
